import { useEffect, useRef } from "react";
import { Truck, Wallet, PackageCheck } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { formatINR } from "@/lib/api";
import { trackViewContent } from "@/lib/tracking/tracker";
import { Eyebrow, Reveal, SectionTitle } from "./Primitives";
import { Cta } from "./Cta";

export const Offer = () => {
  const { content, t } = useSite();
  const p = content.product;
  const pack = content.packs?.[0] || { price: p.price, mrp: p.mrp, label: "1 Jar" };
  const off = pack.mrp > pack.price ? Math.round(((pack.mrp - pack.price) / pack.mrp) * 100) : 0;
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { trackViewContent(); io.disconnect(); }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const rows = [
    { icon: PackageCheck, label: t("pack"), value: `${pack.label} · ${p.pack_size}` },
    p.shipping_info && { icon: Truck, label: t("shipping"), value: p.shipping_info },
    p.payment_info && { icon: Wallet, label: t("payment"), value: p.payment_info },
  ].filter(Boolean);

  return (
    <section id="offer" ref={ref} data-testid="offer-section" className="bg-bone py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid overflow-hidden rounded-[32px] border border-forest/10 bg-white lg:grid-cols-2">
          <div className="relative min-h-[360px] overflow-hidden bg-forest sm:min-h-[460px]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(201,162,39,0.45),transparent_60%)]" aria-hidden />
            <img src={p.images?.[0]} alt={`${p.name} jar`} loading="lazy" decoding="async" width="1000" height="1067"
              className="absolute left-1/2 top-1/2 h-[92%] w-auto max-w-none -translate-x-1/2 -translate-y-1/2 rounded-[28px] object-cover shadow-2xl ring-1 ring-white/20" />
            {off > 0 && (
              <span className="absolute left-6 top-6 rounded-full bg-gold px-4 py-2 text-sm font-extrabold text-forest">{t("save_pct", { n: off })}</span>
            )}
          </div>
          <Reveal className="flex flex-col justify-center p-7 sm:p-12">
            <Eyebrow>{t("offer_eyebrow")}</Eyebrow>
            <SectionTitle testId="offer-product-name">{p.name}</SectionTitle>
            <p className="t-body mt-3 text-sage">{p.category}</p>
            <div data-testid="offer-price" className="mt-8 flex flex-wrap items-end gap-4">
              <span className="t-price text-forest">{formatINR(pack.price)}</span>
              {off > 0 && <span className="pb-2 text-lg text-sage line-through">{t("mrp")} {formatINR(pack.mrp)}</span>}
              {off > 0 && <span className="mb-2.5 text-sm font-semibold text-leaf">{t("you_save", { amount: formatINR(pack.mrp - pack.price) })}</span>}
            </div>
            <dl className="mt-8 divide-y divide-forest/10 border-y border-forest/10">
              {rows.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex gap-4 py-4">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-leaf" aria-hidden />
                  <div><dt className="t-label text-sage">{label}</dt><dd className="t-body mt-1 font-medium text-ink">{value}</dd></div>
                </div>
              ))}
            </dl>
            <div className="mt-8 grid gap-3">
              <Cta kind="consult" name="midpage_consultation" position="offer" className="w-full" />
              <Cta kind="call" name="midpage_call" position="offer" variant="outline" className="w-full" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
